<template>
  <ModalValueHelp
    id="trackvision-broadcast-modal"
    ref="modalBroadcast"
    title="Live Broadcast"
    size="xl"
    :cancel-title="$t('global.action.cancel')"
    :ok-title="$t('trackVideo.broadcast.connect')"
    :attrs="attrsObject"
    :multiple="true"
    :context="contextObject"
    :fullObject="vehicleSelected"
    @update:fullObject="onUpdateModalValue"
    @show="onModalShow"
    @hide="onModalHide"
  >
    <template #alert>
      <div class="mc-modal-broadcast__alert-wrapper">
        <i class="icon-fill_info"></i>
        <i18n path="trackVideo.broadcast.alert" tag="p" class="mc-modal-broadcast__alert">
          <strong>{{ $t('trackVideo.broadcast.oneDirection') }}</strong>
        </i18n>
      </div>
    </template>
    <template v-for="(val, key) in slotFields" #[val.name]="{ item }">
      <!-- eslint-disable-next-line vue/require-component-is -->
      <component
        :is="val.slotComponent"
        :key="key"
        :item="item"
        v-bind="val.customProps ? val.customProps(item) : {}"
      />
    </template>
    <template #customPlaceholderSelected="{ totalSelected }">
      <h6 class="mc-modal-broadcast__placeholder-selected">
        {{
          `(${totalSelected}/${maxBroadcastConcurrent})` + ' ' + $t('global.text.vehicleChoosen')
        }}
      </h6>
    </template>
  </ModalValueHelp>
</template>

<script>
import ModalValueHelp from '@/components/formulate/TrackvisionFilterValueHelp/components/ModalValueHelp.vue'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import VideoStatusBadge from '@/components/VideoStatusBadge.vue'

export default {
  name: 'ModalSelectBroadcast',
  components: { ModalValueHelp, VideoStatusBadge },
  mixins: [Helper],
  data() {
    return {
      vehicleSelected: [],
      selectedVehicleIds: [],
      isModalOpen: false,
      wsMessageHandler: null,
      intervalUpdateWs: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapGetters('video', ['vehicleStatusByVehicleId', 'tempVehicleStatusByVehicleId']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isHasAccessSwitchView() {
      return this.hasAccess(['b3n-trackvision-toggle-video-status:read'])
    },
    isNewView() {
      const isNewView = JSON.parse(localStorage.getItem('live-streaming-switch-view-on')) ?? true
      return isNewView
    },
    broadcasts() {
      return this.$store.state.voip.broadcasts || []
    },
    contextObject() {
      return {
        model: this.selectedVehicleIds,
        label: this.$t('trackVideo.selectVehicles') || 'Vehicles',
        type: 'trackvision-filter-value-help',
        attributes: {},
        visibleValidationErrors: [],
        blurHandler: () => {
          // Empty handler
        },
      }
    },
    attrsObject() {
      return {
        name: 'broadcast-vehicles',
        modalTitle: 'Live Broadcast',
        tableFields: this.vehicleFields,
        trackBy: 'id',
        displayedBy: 'vehicle.licensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        rowCanClick: (vehicleId) => {
          const selectedValues = this.$refs.modalBroadcast.getSelectedValues() ?? []
          if (
            (selectedValues?.length >= this.maxBroadcastConcurrent &&
              !selectedValues.includes(vehicleId)) ||
            // THis below condition is trigger if select row from checkbox
            // cause checkbox use v-model, so the values v-model will auto trigger before rowCanClick is called
            // the impact of this v-model behavior is there is a checkbox that it's actually can't click, but become can click
            // cause above condition is not trigger, so this condition is the final check
            (selectedValues.includes(vehicleId) &&
              selectedValues?.length > this.maxBroadcastConcurrent)
          )
            return false
          return this.getVideoStatusIsOnline(vehicleId)
        },
        filters: [
          {
            label: this.$t('report.fields.fleetType'),
            key: 'vehicleType',
            data: this.$store.state.video.vehicleTypeList.length
              ? this.$store.state.video.vehicleTypeList.map(
                  ({ vehicleTypeId, manufacturer, model, type }) => ({
                    name: (manufacturer ?? '-') + ' / ' + (model ?? '-') + ' / ' + (type ?? '-'),
                    id: vehicleTypeId,
                  })
                )
              : [],
            model: [],
          },
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vgroup',
            data: this.$store.state.video.vgroupsList.length
              ? this.$store.state.video.vgroupsList.map(({ vgroupId, vgroup: { name } }) => ({
                  id: vgroupId,
                  name,
                }))
              : [],
            model: [],
          },
        ],
      }
    },
    slotFields() {
      return this.vehicleFields.filter((v) => v.isSlot)
    },
    vehicleFields() {
      return [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'licensePlate',
        },
        ...((this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          !this.isHasAccessSwitchView) ||
        (this.isHasAccessSwitchView && this.isNewView)
          ? [
              {
                label: 'Status Video',
                key: 'id',
                isSortable: false,
                isSlot: true,
                slotComponent: VideoStatusBadge,
                customProps: (item) => ({
                  isOnline: this.getVideoStatusIsOnline(item.id),
                }),
              },
            ]
          : []),
        {
          label: this.$t('report.fields.fleetType'),
          key: 'vehicleType',
          isSortable: false,
          formatter: (value) => {
            return (
              (value.manufacturer ?? '-') +
              ' / ' +
              (value.model ?? '-') +
              ' / ' +
              (value.type ?? '-')
            )
          },
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
    },
    getVideoStatusIsOnline() {
      return (vehicleId) => {
        const vehicleStatus = this.getVehicleStatusByVehicleId(vehicleId)
        if (this.checkIsDemo(vehicleId)) return true
        if (!vehicleStatus) return false

        let secondEngineOn = 60
        let secondEngineOff = 600
        if (this.getFeatureFlagValue('trackvision_video_offline_acc_on')) {
          secondEngineOn = this.getFeatureFlagValue('trackvision_video_offline_acc_on')
        }
        if (this.getFeatureFlagValue('trackvision_video_offline_acc_off')) {
          secondEngineOff = this.getFeatureFlagValue('trackvision_video_offline_acc_off')
        }

        const lastReceive = vehicleStatus.lastReceive
        const engineOn = vehicleStatus.engineOn
        const diffMinutes = this.getDurationBetween2Dates(lastReceive, this.nowUtc())

        if (engineOn) return diffMinutes < secondEngineOn / 60
        return diffMinutes < secondEngineOff / 60
      }
    },
    maxBroadcastConcurrent() {
      return this.getFeatureFlagValue('trackvision_multivoip_max_concurrent')
    },
  },
  watch: {
    broadcasts: {
      handler(newBroadcast, oldBroadcast) {
        const vehicles = this.getVehicleList({}).data
        const newVehicles = vehicles.filter(({ id }) => {
          return newBroadcast.some(({ vehicleId }) => vehicleId === id)
        })
        this.vehicleSelected = newVehicles
        this.selectedVehicleIds = newVehicles.map(({ id }) => id)
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    checkIsDemo(vehicleId) {
      const dataDemoVideoHistory = this.getFeatureFlagValue('vsms2_trackvision2_playback_demo_id')
      return Object.keys(dataDemoVideoHistory)
        .map((id) => parseInt(id))
        .includes(vehicleId)
    },
    async onUpdateModalValue(value) {
      this.vehicleSelected = value
      this.selectedVehicleIds = value.map(({ id }) => id)
      await this.$nextTick()

      const vehicleBroadcastIdActive = this.$store.state.voip.broadcasts.map(
        (call) => call.vehicleId
      )

      const newVehicle = this.vehicleSelected.filter(
        (v) => !vehicleBroadcastIdActive.includes(v.id)
      )

      const removedVehicleIds = vehicleBroadcastIdActive.filter(
        (id) => !this.selectedVehicleIds.includes(id)
      )

      if (vehicleBroadcastIdActive.length === 0) {
        this.$store.commit('voip/setIsBroadcastActive', true)
      }

      await this.$nextTick()

      for (let i = 0; i < newVehicle.length; i++) {
        const vehicle = newVehicle[i]
        this.$store.dispatch('voip/addBroadcastVehicle', {
          dataNewBroadcast: {
            vehicleId: vehicle.id,
            licensePlate: vehicle.licensePlate,
            driver: vehicle.driver ?? null,
            imei: vehicle.imei,
          },
        })
      }

      for (let i = 0; i < removedVehicleIds.length; i++) {
        this.$store.commit('voip/removeBroadcast', removedVehicleIds[i])
      }
    },
    getVehicleList(opt) {
      const { orderBy, search, vehicleType, vgroup } = opt

      const allVehicles = []
      this.$store.state.video.fleets.forEach((data) => {
        const groups = data.vehicle.vehicle2Vgroup
        const isBsj = this.getDeviceType(data.vehicle.device.deviceCode) === 'bsj'
        if (isBsj) {
          allVehicles.push({
            licensePlate: data.parent
              ? data.parent?.vehicle?.licensePlate
              : data.vehicle.licensePlate,
            vehicle2Vgroup: groups,
            vehicleType: data.vehicle.vehicleType,
            id: data.vehicleId,
            imei: data.vehicle.imei,
            deviceType: this.getDeviceType(data.vehicle.device.deviceCode),
            driver: data?.parent
              ? data?.parent?.vehicle?.driver1?.fullname ?? null
              : data.vehicle.driver1?.fullname ?? null,
          })
        }
      })

      let result = [...allVehicles]

      // apply filter
      result = result.filter((v) => {
        if (!vehicleType || vehicleType.length === 0) return true
        return vehicleType?.includes(v.vehicleType.vehicleTypeId)
      })
      result = result.filter((v) => {
        if (!vgroup || vgroup.length === 0) return true
        return v.vehicle2Vgroup.some((vg) => vgroup.includes(vg.vgroupId))
      })

      // apply search
      if (search) {
        result = result.filter((v) => {
          return this.isContains(search, v.licensePlate)
        })
      }

      result.sort((a, b) => {
        // First criterion: Device Online/Offline status (online first)
        const aIsOnline = this.getVideoStatusIsOnline(a.id)
        const bIsOnline = this.getVideoStatusIsOnline(b.id)
        if (aIsOnline !== bIsOnline) {
          return aIsOnline ? -1 : 1 // Online comes first
        }
        return a.licensePlate.toLowerCase().localeCompare(b.licensePlate.toLowerCase())
      })

      return {
        data: result,
        ids: result.map((data) => data.id), // ids vehicle
        pagination: {
          page: 1,
          show: result.length,
          total: result.length,
        },
      }
    },
    isContains(search, name) {
      const searchContains = RegExp(search ?? '', 'gi').exec(name ?? '')
      return searchContains != null && searchContains.length > 0
    },
    getVehicleStatusByVehicleId(vehicleId) {
      if (this.checkIsDemo(vehicleId)) {
        return {
          ...this.vehicleStatusByVehicleId(vehicleId),
          calculatedMotionStatus: 'M',
        }
      }
      return this.vehicleStatusByVehicleId(vehicleId)
    },
    calculatedTempWsData(vehicleId, data) {
      let calculatedMotionStatus = data.calculatedMotionStatus

      if (data.calculatedMotionStatus === 'A' && !this.hasAccess(['assisted_motion_status:read'])) {
        calculatedMotionStatus = 'P'
      }

      this.$store.commit('video/setTempVehicleStatusesByVehicleId', {
        vehicleId,
        data: {
          ...this.tempVehicleStatusByVehicleId(vehicleId),
          lastReceive: data.lastReceive,
          latitude: data.latitude,
          longitude: data.longitude,
          calculatedMotionStatus,
          engineOn: data.engineOn,
        },
      })
    },

    handleWebSocketMessage(message) {
      const signal = typeof message === 'string' ? JSON.parse(message) : message
      if (signal.type.startsWith('gps-')) {
        const vehicleIdGps = Number(signal.type.slice(4))
        const vehicleIds = this.getVehicleList({}).data.map((v) => v.id)
        if (!vehicleIds.includes(vehicleIdGps)) return
        this.calculatedTempWsData(vehicleIdGps, signal.payload)
      }
    },
    async onModalShow() {
      this.isModalOpen = true
      if (this.$route.name === 'Live Streaming Trackvision') return

      this.wsMessageHandler = this.handleWebSocketMessage.bind(this)
      await this.$store.dispatch('ws/registerMessageHandler', this.wsMessageHandler)

      this.intervalUpdateWs = setInterval(async () => {
        Object.entries(this.$store.state.video.tempVehicleStatuses).forEach(
          ([vehicleId, vehicleStatus]) => {
            this.$store.commit('video/setVehicleStatusesByVehicleId', {
              vehicleId: vehicleId,
              data: {
                calculatedMotionStatus: vehicleStatus.calculatedMotionStatus,
                lastReceive: vehicleStatus.lastReceive,
                engineOn: vehicleStatus.engineOn,
              },
            })
          }
        )
        await this.$nextTick()
        this.$store.commit('video/resetTempVehicleStatuses')
        this.$forceUpdate()
      }, 4000)
    },
    onModalHide() {
      clearInterval(this.intervalUpdateWs)
      this.isModalOpen = false
      if (this.$route.name === 'Live Streaming Trackvision') return
      if (this.wsMessageHandler) {
        this.$store.dispatch('ws/unregisterMessageHandler', this.wsMessageHandler)
      }
    },
  },
}
</script>
<style lang="scss">
#trackvision-broadcast-modal .mc-value-help {
  margin-top: 56px;
}

.mc-modal-brodcast__modal-contents {
  padding: 32px;
}
.mc-modal-broadcast__alert-wrapper {
  background-color: $cyan-100;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  .icon-fill_info::before {
    color: $cyan;
    font-size: $font-size-lg * 1.25;
  }
  .mc-modal-broadcast__alert {
    font-size: 14px;
    line-height: 20px;
    color: #333333;
    margin: 0;
  }
}

.mc-modal-broadcast__placeholder-selected {
  font-weight: 500;
  font-size: $font-size-lg;
}
</style>
